import { HttpStatusCode } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AlertDisplayType } from "@commonEnums/AlertDisplayType";
import { AlertService } from '@commonServices/alert.service';
import { RequestService } from '@commonServices/request.service';
import { AlertPositionType } from '@enums/alertPositionType';
import { Client as CatalogAPI, FtpCatalogDto, FtpCatalogPublishSettingsDto } from '@interfaces/HttpClient/CatalogApiModels';
import { firstValueFrom } from 'rxjs';
import { AdministrationService } from './administration.service';
import { TranslationService } from './translation.service';

@Injectable()
export class FtpCatalogService {

	constructor(
		private catalogApi: CatalogAPI,
		private requestService: RequestService,
		private administrationService: AdministrationService,
		private translationService: TranslationService,
		private alertService: AlertService<AlertPositionType>
	) { }

	async getFtpCatalogInfo(
		catalogId?: number
	): Promise<FtpCatalogDto> {
		const id = catalogId ? catalogId : this.administrationService.getId();
		try {
			return await firstValueFrom(this.catalogApi
				.ftpCatalog_GetFtpCatalogInfo(id));
		} catch (e) {
			return this.requestService.errorHandling(e, null, [
				{ status: HttpStatusCode.NoContent, return: undefined }
			]);
		}
	}

	async createFtpCatalog(
		catalogId?: number
	): Promise<FtpCatalogDto> {
		const id = catalogId ? catalogId : this.administrationService.getId();
		try {
			return await firstValueFrom(this.catalogApi
				.ftpCatalog_CreateFtpCatalog(id));
		} catch (e) {
			return this.requestService.errorHandling(e, null, [
				{ status: HttpStatusCode.NoContent, return: undefined }
			]);
		}
	}

	async deleteFtpCatalog(
		catalogId?: number
	): Promise<void> {
		const id = catalogId ? catalogId : this.administrationService.getId();
		try {
			return await firstValueFrom(this.catalogApi
				.ftpCatalog_DeleteFtpCatalog(id));
		} catch (e) {
			return this.requestService.errorHandling(e, null, [
				{ status: HttpStatusCode.NoContent, return: undefined }
			]);
		}
	}

	async renewFtpCatalogPassword(
		catalogId?: number
	): Promise<FtpCatalogDto> {
		const id = catalogId ? catalogId : this.administrationService.getId();
		try {
			return await firstValueFrom(this.catalogApi
				.ftpCatalog_RenewFtpCatalogPassword(id));
		} catch (e) {
			return this.requestService.errorHandling(e, null, [
				{ status: HttpStatusCode.NoContent, return: undefined }
			]);
		}
	}

	async getFtpCatalogPublishSettings(catalogId?: number): Promise<FtpCatalogPublishSettingsDto> {
		const id = catalogId ? catalogId : this.administrationService.getId();
		try {
			return await firstValueFrom(this.catalogApi
				.ftpCatalog_GetFtpCatalogPublishSettings(id));
		} catch (e) {
			return this.requestService.errorHandling(e, null, [
				{ status: HttpStatusCode.NoContent, return: undefined }
			]);
		}
	}

	async updateFtpCatalogPublishSettings(updatedSettings: FtpCatalogPublishSettingsDto): Promise<FtpCatalogPublishSettingsDto> {
		try {
			const update = await firstValueFrom(this.catalogApi
				.ftpCatalog_UpdateFtpCatalogPublishSettings(updatedSettings));
			this.alertService.sendMessage(AlertPositionType.Global, AlertDisplayType.Success, this.translationService.getByKey(
				"catalog.upload.automatisation.release.save"
			))
			return update;
		} catch (e) {
			return this.requestService.errorHandling(e, null, [
				{ status: HttpStatusCode.NoContent, return: undefined }
			]);
		}
	}
}
